<script>
import emojis from './emojisGroup.json';
import { emojiLocaleKey, loadEmojiLocaleData } from './emojiLocaleData';
import { normalizeEmojiSearch } from './pickerHelper';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
const SEARCH_KEY = 'Search';
const RECENT_KEY = 'Recentes';
const RECENT_STORAGE_KEY = 'chatplan_recent_emojis';
const MAX_RECENT = 32;

export default {
  components: { FluentIcon, NextButton },
  props: {
    onClick: {
      type: Function,
      default: () => {},
    },
    showRemoveButton: {
      type: Boolean,
      default: false,
    },
    accentColor: {
      type: String,
      default: 'var(--widget-color, var(--n-brand))',
    },
  },
  data() {
    return {
      selectedKey: RECENT_KEY,
      emojis,
      search: '',
      recentEmojis: [],
      emojiLocaleData: null,
    };
  },
  computed: {
    categories() {
      if (this.recentEmojis.length > 0) {
        const recentCategory = {
          name: RECENT_KEY,
          slug: 'recent',
          emojis: this.recentEmojis,
        };
        return [recentCategory, ...this.emojis];
      }
      return [...this.emojis];
    },
    filterEmojisByCategory() {
      if (this.selectedKey === RECENT_KEY && this.recentEmojis.length > 0) {
        return this.recentEmojis;
      }
      const selectedCategoryName = this.emojis.find(category =>
        category.name === this.selectedKey ? category.name : null
      );
      return selectedCategoryName?.emojis;
    },
    filterAllEmojisBySearch() {
      const term = normalizeEmojiSearch(this.search);
      return this.emojis.map(category => {
        const allEmojis = category.emojis.filter(emoji => {
          const localized = this.emojiLocaleData?.emojis.get(
            emojiLocaleKey(emoji.emoji)
          );
          const searchText = [
            emoji.slug.replaceAll('_', ' '),
            emoji.name,
            localized?.label,
            ...(localized?.tags || []),
          ]
            .filter(Boolean)
            .join(' ');
          return normalizeEmojiSearch(searchText).includes(term);
        });
        return allEmojis.length > 0
          ? { ...category, emojis: allEmojis }
          : { ...category, emojis: [] };
      });
    },
    hasNoSearch() {
      return this.selectedKey !== SEARCH_KEY && this.search === '';
    },
    hasEmptySearchResult() {
      return this.filterAllEmojisBySearch.every(
        category => category.emojis.length === 0
      );
    },
    activeIndicatorStyle() {
      return { backgroundColor: this.accentColor };
    },
  },
  watch: {
    search() {
      this.selectedKey = 'Search';
    },
    selectedKey() {
      return this.selectedKey === 'Search' ? this.focusSearchInput() : null;
    },
    '$i18n.locale': {
      handler() {
        this.loadLocaleData();
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadRecentEmojis();
    if (this.recentEmojis.length > 0) {
      this.selectedKey = RECENT_KEY;
    } else {
      this.focusSearchInput();
    }
  },
  methods: {
    async loadLocaleData() {
      this.emojiLocaleData = await loadEmojiLocaleData(this.$i18n.locale);
    },
    changeCategory(categoryName) {
      if (categoryName === 'Search') {
        this.selectedKey = 'Search';
        this.search = '';
        this.$refs.emojiItem.scrollTo({ top: 0, behavior: 'smooth' });
        this.focusSearchInput();
        return;
      }

      this.selectedKey = categoryName;

      // Scroll into view
      this.$nextTick(() => {
        const el = this.$refs[`category-${categoryName}`];
        if (el && el[0]) {
          const container = this.$refs.emojiItem;
          const offsetTop = el[0].offsetTop;
          container.scrollTo({ top: offsetTop, behavior: 'smooth' });
        }
      });
    },
    getCategoryIcon(slug) {
      const map = {
        smileys_emotion: 'i-ph-smiley',
        people_body: 'i-ph-users',
        animals_nature: 'i-ph-leaf',
        food_drink: 'i-ph-coffee',
        travel_places: 'i-ph-car',
        activities: 'i-ph-soccer-ball',
        objects: 'i-ph-lightbulb',
        symbols: 'i-ph-hash',
        flags: 'i-ph-flag',
      };
      return map[slug] || 'i-ph-star';
    },
    getFirstEmojiByCategoryName(categoryName) {
      if (categoryName === RECENT_KEY) {
        return this.recentEmojis.length > 0 ? this.recentEmojis[0].emoji : '🕐';
      }
      const categoryItem = this.emojis.find(category =>
        category.name === categoryName ? category : null
      );
      return categoryItem ? categoryItem.emojis[0].emoji : '';
    },
    focusSearchInput() {
      this.$nextTick(() => {
        this.$refs.searchbar.focus();
      });
    },
    handleEmojiClick(emoji) {
      this.saveRecentEmoji(emoji);
      this.onClick(emoji);
    },
    loadRecentEmojis() {
      try {
        const legacyStorageKey = ['sac', 'plan_recent_emojis'].join('');
        const stored =
          localStorage.getItem(RECENT_STORAGE_KEY) ||
          localStorage.getItem(legacyStorageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          this.recentEmojis = parsed
            .sort((a, b) => b.count - a.count)
            .slice(0, MAX_RECENT)
            .map(item => ({
              emoji: item.emoji,
              slug: item.slug || item.emoji,
            }));
        }
      } catch {
        this.recentEmojis = [];
      }
    },
    saveRecentEmoji(emoji) {
      try {
        const stored = localStorage.getItem(RECENT_STORAGE_KEY);
        let recents = stored ? JSON.parse(stored) : [];
        const existing = recents.find(r => r.emoji === emoji);
        if (existing) {
          existing.count = (existing.count || 1) + 1;
        } else {
          recents.push({ emoji, slug: emoji, count: 1 });
        }
        recents.sort((a, b) => b.count - a.count);
        recents = recents.slice(0, MAX_RECENT);
        localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(recents));
        this.loadRecentEmojis();
      } catch {
        // localStorage unavailable
      }
    },
  },
};
</script>

<template>
  <div
    role="dialog"
    class="emoji-dialog absolute bottom-full mb-3 left-0 bg-n-background shadow-2xl rounded-2xl border border-n-slate-3 outline-none box-content h-[25rem] w-[26rem] z-20 overflow-hidden"
  >
    <div class="flex flex-col h-full">
      <div
        class="emoji-dialog--header relative w-full pt-1 pb-0 px-2 bg-n-background border-b border-n-slate-3 dark:border-n-slate-8"
      >
        <ul
          class="flex relative list-none m-0 overflow-x-auto p-0 w-full justify-between [scrollbar-width:none] [-ms-overflow-style:none]"
        >
          <li class="relative flex-1 group">
            <button
              v-if="recentEmojis.length > 0"
              class="emoji--header-item"
              :class="{ active: selectedKey === 'Recentes' }"
              :title="$t('EMOJI.GROUPS.RECENTES') || 'Recentes'"
              @click="changeCategory('Recentes')"
            >
              <i class="i-ph-clock text-[20px]" />
            </button>
            <div
              v-if="recentEmojis.length > 0"
              class="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-sm transition-opacity"
              :class="selectedKey === 'Recentes' ? 'opacity-100' : 'opacity-0'"
              :style="activeIndicatorStyle"
            />
          </li>
          <li
            v-for="category in emojis"
            :key="category.slug"
            class="relative flex-1 group"
          >
            <button
              class="emoji--header-item"
              :class="{ active: selectedKey === category.name }"
              :title="
                $t(`EMOJI.GROUPS.${category.slug.toUpperCase()}`) ||
                category.name
              "
              @click="changeCategory(category.name)"
            >
              <i :class="getCategoryIcon(category.slug)" class="text-[20px]" />
            </button>
            <div
              class="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-sm transition-opacity"
              :class="
                selectedKey === category.name ? 'opacity-100' : 'opacity-0'
              "
              :style="activeIndicatorStyle"
            />
          </li>
        </ul>
      </div>

      <div class="mx-3 mt-2 mb-2 sticky top-0 z-20 bg-n-background pt-1 pb-1">
        <div
          class="flex items-center w-full bg-n-slate-2 dark:bg-n-slate-8 rounded-full px-3 h-9"
        >
          <FluentIcon
            icon="search"
            size="16"
            class="text-n-slate-10 dark:text-n-slate-11 flex-shrink-0"
          />
          <input
            ref="searchbar"
            v-model="search"
            type="text"
            class="flex-1 !bg-transparent !border-none !outline-none focus:!ring-0 !shadow-none !border-transparent focus:!border-transparent text-sm ml-2 !mb-0 !h-8 text-n-slate-12 dark:text-white ![background:transparent] ![border:none] ![box-shadow:none] ![outline:none]"
            :placeholder="$t('EMOJI.PLACEHOLDER') || 'Pesquisar emoji'"
          />
          <NextButton
            v-if="showRemoveButton"
            faded
            sm
            slate
            class="flex-shrink-0 !ml-2"
            :label="$t('EMOJI.REMOVE')"
            @click="onClick('')"
          />
        </div>
      </div>
      <div
        v-if="hasNoSearch"
        ref="emojiItem"
        class="emoji-item scroll-smooth pb-10 [scrollbar-width:none] [-ms-overflow-style:none]"
      >
        <div
          v-for="category in categories"
          :ref="`category-${category.name}`"
          :key="category.slug"
        >
          <h5
            class="text-sm text-n-slate-11 font-medium leading-normal m-0 py-1 px-3 sticky top-0 bg-n-background z-10"
          >
            {{
              $t(`EMOJI.GROUPS.${category.slug.toUpperCase()}`) &&
              $t(`EMOJI.GROUPS.${category.slug.toUpperCase()}`) !==
                `EMOJI.GROUPS.${category.slug.toUpperCase()}`
                ? $t(`EMOJI.GROUPS.${category.slug.toUpperCase()}`)
                : category.name
            }}
          </h5>
          <div class="emoji--grid">
            <button
              v-for="item in category.emojis"
              :key="item.slug"
              class="emoji--item"
              track-by="$index"
              @click="handleEmojiClick(item.emoji)"
            >
              {{ item.emoji }}
            </button>
          </div>
        </div>
      </div>
      <div v-else ref="emojiItem" class="emoji-item pb-10">
        <div v-for="category in filterAllEmojisBySearch" :key="category.slug">
          <h5
            v-if="category.emojis.length > 0"
            class="text-sm text-n-slate-11 font-medium leading-normal m-0 py-1 px-3"
          >
            {{
              $t(`EMOJI.GROUPS.${category.slug.toUpperCase()}`) &&
              $t(`EMOJI.GROUPS.${category.slug.toUpperCase()}`) !==
                `EMOJI.GROUPS.${category.slug.toUpperCase()}`
                ? $t(`EMOJI.GROUPS.${category.slug.toUpperCase()}`)
                : category.name
            }}
          </h5>
          <div v-if="category.emojis.length > 0" class="emoji--grid">
            <button
              v-for="item in category.emojis"
              :key="item.slug"
              class="emoji--item"
              track-by="$index"
              @click="handleEmojiClick(item.emoji)"
            >
              {{ item.emoji }}
            </button>
          </div>
        </div>
        <div
          v-if="hasEmptySearchResult"
          class="items-center flex flex-col h-[13.25rem] justify-center"
        >
          <div class="text-n-slate-11 mb-2">
            <FluentIcon icon="emoji" size="48" />
          </div>
          <span class="text-n-slate-11 text-sm font-medium">
            {{ $t('EMOJI.NOT_FOUND') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@tailwind components;

@layer components {
  .box-shadow-dark {
    box-shadow:
      0 0 0 1px #212222,
      0 0 1px 2px #4c5155;
  }
}
</style>

<style lang="scss">
.emoji-dialog {
  &::before {
    @apply absolute -bottom-3 h-3 w-6 bg-n-slate-3 content-[""];
    clip-path: polygon(50% 100%, 0% 0%, 100% 0%);
  }
}

.emoji--item {
  @apply bg-transparent border-0 rounded-lg cursor-pointer text-[1.6rem] m-0 pr-0 pl-0 hover:bg-n-slate-3 dark:hover:bg-n-slate-8 flex items-center justify-center transition-colors;
}

.emoji--header-item {
  @apply bg-transparent border-0 cursor-pointer w-full h-[2.5rem] flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 transition-colors focus:outline-none focus:ring-0;
}

.emoji--header-item.active {
  color: var(--widget-color, var(--n-brand));
}

.emoji--grid {
  @apply box-border px-3 py-1 grid grid-cols-9 gap-1;

  .emoji--item {
    @apply h-[2.2rem] w-[2.2rem] leading-none text-center;
  }
}

.emoji-item {
  @apply relative h-[20rem] overflow-y-auto overflow-x-hidden;
}

/* Scrollbar styling */
.emoji-item::-webkit-scrollbar {
  width: 6px;
}
.emoji-item::-webkit-scrollbar-thumb {
  @apply bg-n-slate-4 dark:bg-n-slate-8 rounded;
}
.emoji-item::-webkit-scrollbar-track {
  background: transparent;
}
</style>
